import {
  Autocomplete,
  AutocompleteChangeReason,
  CircularProgress,
  InputAdornment,
  Typography,
  Divider,
  MenuItem,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Props, State } from "./interfaces";
import { StyledTextfield } from "./styles";
import { PropsFilter } from "../../models/api/props-filter";
import { RefModel } from "../../models/base/ref-model";

import { StyledAutocompleteTextfield } from "../AutocompleteQuickFilter/styles";

const PAGE_SIZE = 5;

export const allAutocompliteOption = "all";
export const notInTheListOptionID = "3e9d6e3b-f0c0-46fc-b870-a060fae371ed";

function AutocompleteDropdown<T>({
  labelKey, // translation key for label
  field,
  fieldProp = "name",
  fieldPropSecondDepth,
  isMultiSelect,
  serviceApi,
  propsFilter,
  apiLoadItemsPath,
  propsItem,
  selectedValue,
  handleChange,
  sxProps,
  inputVariant = "outlined",
  disabled,
  required,
  excludeIds,
  childField,
  childFieldProp = "name",
  noLabel = false,
  inputStyle,
  disableOption,
  quickFilterLike = false,
  hasError = false,
  withAllOption = false,
  allOptionAsDefault = false,
  resetToSelectedValueOnBlur,
  multipleChildFields,
  translationRoutePath,
  taggedMenuOption,
  acceptNewValue,
  helperText,
  allowNotInTheListOption,
  endAdornment,
  textFieldProps,
  compareByField,
}: Props<T>) {
  const theme = useTheme();

  const [state, setState] = useState<State>({
    query: "",
    prevQuery: "",
    value: isMultiSelect ? [] : null,
    loading: false,
    error: "",
    open: false,
    items: [],
    totalItems: 0,
    pageNumber: 0,
  });

  const { t } = useTranslation();
  const optionAll = { id: allAutocompliteOption, name: `${t("common_words.all")}` };
  const notInTheListOption = { id: notInTheListOptionID, name: `${t("common_words.not_in_the_list")}` };

  const handleChangePage = (pageNumber: number): void => setState((state) => ({ ...state, pageNumber }));

  const handleChangeQuery = (query: string): void =>
    setState((state) => ({
      ...state,
      pageNumber: 0,
      prevQuery: state.query,
      query,
    }));

  const handleChangeValue = (value: RefModel & [], reason: AutocompleteChangeReason) => {
    setState((state) => ({
      ...state,
      value,
      ...(reason === "clear" ? { query: "", prevQuery: "" } : {}),
    }));
  };

  //#region load data
  const loadData = useCallback(
    async (query = "", pageNumber = 0, signal: AbortSignal) => {
      if (apiLoadItemsPath) serviceApi.useLoadItemsPath(apiLoadItemsPath);

      const response = await serviceApi.loadItems(
        // if it becomes necessary, the filter object can be passed as prop
        // so that the caller defines the necessary filter
        {
          ids: [],
          notIds: excludeIds ?? [],
          createdByUsers: [],
          modifiedByUsers: [],
          isDeleted: false,
          ...(propsFilter ?? {}),
        } as PropsFilter,
        query,
        [...serviceApi.defaultSort],
        { pageNumber, pageSize: PAGE_SIZE },
        false,
        60,
        signal
      );

      return response;
    },
    [serviceApi, apiLoadItemsPath, propsFilter, excludeIds]
  );

  //#region loadData ---- ON INIT
  const loadDataOnInit = useCallback(
    async (query: string, signal: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { items, totalItems, error } = await loadData(query, 0, signal);

        if (!signal.aborted)
          setState((state) => ({
            ...state,
            loading: false,
            error,
            items: withAllOption
              ? [optionAll, ...(allowNotInTheListOption ? [notInTheListOption] : []), ...items]
              : [...items, ...(allowNotInTheListOption ? [notInTheListOption] : [])],
            totalItems,
          }));
      } catch (error: any) {
        if (!signal.aborted) setState((state) => ({ ...state, loading: false, error }));
      }
    },
    [loadData, withAllOption]
  );

  //#region loadData --- ON SCROLL
  const loadDataOnScroll = useCallback(
    async (pageNumber: number, signal: AbortSignal): Promise<void> => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { items, totalItems, error } = await loadData(state.query, pageNumber, signal);

        if (!signal.aborted)
          setState((state) => ({
            ...state,
            loading: false,
            error,
            items: [...state.items, ...items],
            totalItems,
          }));
      } catch (error: any) {
        if (!signal.aborted) setState((state) => ({ ...state, loading: false, error }));
      }
    },
    [loadData, state.query]
  );

  const setOpen = (value: boolean): void => setState((state) => ({ ...state, open: value }));

  //#region reset state on close AutocompleteDropdown
  const closeAndReset = () =>
    setState((state) => ({
      ...state,
      open: false,
      pageNumber: 0,
      items: [],
      totalItems: 0,
    }));

  // handle selectedValue when compareByField is used - load and match item
  useEffect(() => {
    if (!selectedValue || !compareByField) return;

    const abortController = new AbortController();

    const loadAndMatchItem = async () => {
      try {
        const { items } = await loadData("", 0, abortController.signal);

        const matchingItem = items.find((item) => {
          const itemAny = item as any;
          if (itemAny[compareByField] && typeof itemAny[compareByField] === "object") {
            return itemAny[compareByField].id === selectedValue.id;
          }
          return itemAny[compareByField] === selectedValue.id || item.id === selectedValue.id;
        });

        if (matchingItem && !abortController.signal.aborted) {
          const queryValue = selectedValue.name || selectedValue[field] || "";

          setState((state) => ({
            ...state,
            value: matchingItem as any,
            query: queryValue,
            prevQuery: queryValue,
          }));
        }
      } catch (error) {
        console.log("AutocompleteDropdown loadAndMatchItem error", error);
      }
    };

    loadAndMatchItem();

    return () => {
      abortController.abort();
    };
  }, [selectedValue, field, compareByField, loadData]);

  // handle selectedValue without compareByField
  useEffect(() => {
    if (compareByField) return; // Skip if compareByField is handling it

    if (selectedValue) {
      setState((state) => ({
        ...state,
        value: selectedValue,
        query: selectedValue[field] ?? "",
        prevQuery: selectedValue[field] ?? "",
      }));
    } else {
      setState((state) => ({
        ...state,
        value: null,
        query: "",
        prevQuery: "",
      }));
    }
  }, [selectedValue, field, compareByField]);

  useEffect(() => {
    if (allOptionAsDefault) {
      setState((state) => ({
        ...state,
        value: optionAll as any,
        query: "",
        prevQuery: "",
      }));
    }
  }, []);

  useEffect(() => {
    if (propsItem && propsItem[field]) {
      if (typeof propsItem[field] === "string") {
        setState((state) => ({
          ...state,
          value: propsItem,
          query: propsItem[field] ?? "",
          prevQuery: propsItem[field] ?? "",
        }));
      } else {
        setState((state) => ({
          ...state,
          value: propsItem[field],
          query: translationRoutePath
            ? t(`${translationRoutePath}.${propsItem[field][fieldProp]}`)
            : propsItem[field][fieldProp] ?? "",
          prevQuery: translationRoutePath
            ? t(`${translationRoutePath}.${propsItem[field][fieldProp]}`)
            : propsItem[field][fieldProp] ?? "",
        }));
      }
    }
  }, [propsItem, field, fieldProp, translationRoutePath]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    if (state.open) {
      // onInit
      if (state.pageNumber === 0 && state.query === state.prevQuery)
        loadDataOnInit(state.query, abortController.signal);

      // onSearch
      if (state.pageNumber === 0 && state.query !== state.prevQuery)
        timer = setTimeout(() => loadDataOnInit(state.query, abortController.signal), 500);

      // onScroll
      if (state.pageNumber > 0) loadDataOnScroll(state.pageNumber, abortController.signal);
    }

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [state.open, state.query, state.prevQuery, state.value, state.pageNumber, loadDataOnInit, loadDataOnScroll]);

  //#region get option label
  const _getOptionLabel = useCallback(
    (option: any): string => {
      if (option.id === allAutocompliteOption) {
        return `${t("common_words.all")}`;
      }
      if (option[fieldProp]) {
        if (!!childField && option[childField] && !!childFieldProp) {
          return `${option[fieldProp]} (${option[childField][childFieldProp]})`;
        } else if (!!childField && option[childField]) {
          return `${option[fieldProp]} (${option[childField]})`;
        } else if (!!multipleChildFields && multipleChildFields.length > 0) {
          //
        } else if (fieldPropSecondDepth) {
          return option[fieldProp][fieldPropSecondDepth];
        } else {
          if (translationRoutePath) {
            return t(`${translationRoutePath}.${option[fieldProp]}`);
          } else {
            return option[fieldProp];
          }
        }
      }
      if (option.email) {
        return option.email;
      }
      return "";
    },
    [childField, childFieldProp, fieldProp, multipleChildFields, t, taggedMenuOption, translationRoutePath]
  );

  return (
    <Autocomplete
      aria-required={required}
      open={state.open}
      disabled={disabled}
      onOpen={() => !disabled && setOpen(true)}
      loading={state.loading}
      onClose={closeAndReset}
      sx={{ ...sxProps }}
      filterOptions={(options) => options}
      multiple={isMultiSelect}
      value={state.value}
      onChange={(_, value, reason) => {
        if (acceptNewValue) {
          if (acceptNewValue(value)) {
            handleChange(field, value);
            handleChangeValue(value, reason);
          }
        } else {
          if (value) {
            handleChange(field, value);
            handleChangeValue(value, reason);
          }
        }
      }}
      onInputChange={(_, value) => handleChangeQuery(value)}
      ListboxProps={{
        onScroll: ({ currentTarget: { scrollTop, clientHeight, scrollHeight } }: React.SyntheticEvent) => {
          const { pageNumber, totalItems } = state;
          const maxPages = Math.ceil(totalItems / PAGE_SIZE);

          if (scrollHeight - Math.ceil(scrollTop) - 1 <= clientHeight && pageNumber < maxPages - 1 && !state.loading)
            handleChangePage(pageNumber + 1);
        },
        role: "list-box",
        style: { maxHeight: "150px", background: "white" },
      }}
      onBlur={() => {
        resetToSelectedValueOnBlur && handleChangeValue(selectedValue, "blur");
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id || option.id === value || option.name === value}
      getOptionDisabled={disableOption}
      getOptionLabel={(option) => _getOptionLabel(option)}
      renderOption={(props, option) => {
        const optionLabel = _getOptionLabel(option);
        return (
          <MenuItem {...props}>
            <Grid style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Typography>{optionLabel}</Typography>
              {taggedMenuOption && taggedMenuOption.id === option.id && taggedMenuOption.tag}
            </Grid>
          </MenuItem>
        );
      }}
      options={[...state.items]}
      renderInput={(params) =>
        quickFilterLike && !isMultiSelect ? (
          <StyledAutocompleteTextfield
            {...params}
            required={required}
            placeholder={t("components.autocomplete_dropdown.placeholders.please_select")}
            InputProps={{
              ...params.InputProps,
              disabled,
              ...(labelKey && {
                startAdornment: (
                  <InputAdornment
                    position={"start"}
                    style={{ display: "flex", height: "100%", gap: "5px" }}
                  >
                    <Typography>{labelKey}</Typography>
                    <Divider
                      orientation={"vertical"}
                      flexItem
                    />
                  </InputAdornment>
                ),
              }),
              endAdornment: (
                <>
                  {state.loading && (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  )}
                  {endAdornment}
                  {!disabled && params.InputProps.endAdornment}
                </>
              ),
              style: {
                ...inputStyle,
              },
            }}
            style={{
              maxWidth: "unset",
            }}
            error={hasError}
            helperText={helperText ?? ""}
          />
        ) : (
          <StyledTextfield
            {...params}
            error={hasError}
            required={required}
            label={!noLabel && labelKey && `${t(`${labelKey}`)}`}
            {...(noLabel && { InputLabelProps: { shrink: false } })}
            variant={inputVariant}
            placeholder={t("components.autocomplete_dropdown.placeholders.please_select")}
            InputProps={{
              ...params.InputProps,
              disabled,
              endAdornment: (
                <>
                  {state.loading && (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  )}
                  {endAdornment}
                  {!disabled && params.InputProps.endAdornment}
                  {taggedMenuOption &&
                    selectedValue &&
                    taggedMenuOption.id === selectedValue.id &&
                    taggedMenuOption.displayTagWhenSelected &&
                    taggedMenuOption.tag}
                </>
              ),
              style: {
                ...inputStyle,
              },
            }}
            helperText={helperText ?? ""}
            {...textFieldProps}
          />
        )
      }
    />
  );
}

export default AutocompleteDropdown;
